<template>
	<div class="add-client-modal">
		<app-loader
			v-if="isLoading"
			:style="loaderStyle"
		/>
		<div class="add-client-modal__search">
			<app-input
				ref="$input"
				v-model="filter"
				class="add-client-modal__search-input"
				:placeholder="$t('search_short')"
			/>
			<div
				v-if="props.showCloseIcon"
				class="add-client-modal__close"
			>
				<app-tooltip :text="$t('close_esc_label')">
					<template #activator>
						<app-icon
							name="close"
							width="20px"
							color="#D2D2D2"
							hover-color="#499259"
							cursor="pointer"
							@click="emit('close')"
						/>
					</template>
				</app-tooltip>
			</div>
		</div>
		<div class="add-client-modal__header">
			<div class="add-client-modal__header-title">
				{{ $t('clients_label') }}
			</div>
		</div>
		<div
			class="add-client-modal__body"
			ref="$body"
		>
			<div
				v-for="client in filteredClients"
				class="add-client-modal__body-item"
				:key="client.Id"
			>
				<app-checkbox
					:style="{ height: '16px', marginRight: '8px' }"
					:value="isClientAdded(client.Id)"
					text=""
					@input="onChangeClientStage($event, client.Id)"
				/>
				<div
					class="add-client-modal__body-item-text"
					@click="onChangeClientStageFromName(client.Id)"
					v-html="getClientLabel(client)"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	computed,
	nextTick,
	onMounted,
	ref,
	watch
} from "vue";
import AppCheckbox from "@/components/ui-kit/checkbox/AppCheckbox.vue";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import { ProjectApi } from "@/ api/ProjectApi";
import { AttachClient } from "@/ api/ClientsApi";
import { projectPageStore } from "@/store/project-page";
import { debounce } from "lodash";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import { escapeHtml, highlightSearchText } from "@/utils/textHelpers";

const props = defineProps<{
	projectId: number,
	addedClients: { Id: number, Name: string }[],
	showCloseIcon?: boolean,
}>()

const emit = defineEmits(['close'])

const $input = ref<typeof AppInput | null>(null)
const $body = ref<HTMLDivElement | null>(null)

const store = projectPageStore()

const isLoading = ref(true)
const filter = ref('')
const appliedFilter = ref('')
const take = 60
const skip = ref(0)
const canLoadNext = ref(true)
const isClientsListInit = ref(false)
const clients = ref<AttachClient[] | null>(null)

const addedIds = computed(() => new Set((props.addedClients || []).map(c => c.Id)))

const filteredClients = computed<AttachClient[]>(() => {
	const list = clients.value || []
	const added = list.filter(c => addedIds.value.has(c.Id))
	const rest = list.filter(c => !addedIds.value.has(c.Id))
	return [...added, ...rest]
})

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))

const getClientLabel = (client: AttachClient) => {
	const address = client.Address?.[0] ? `(${client.Address[0]})` : ''
	return highlightSearchText(appliedFilter.value, escapeHtml(`${client.Name ?? ''}${address}`))
}

const onFetchClients = async () => {
	try {
		if (canLoadNext.value) {
			isLoading.value = true
			const requestedFilter = filter.value
			const clientsArray = await ProjectApi.getAttachClientsList(
				props.projectId,
				requestedFilter,
				skip.value,
				take
			)
			appliedFilter.value = requestedFilter
			clients.value = skip.value === 0
				? clientsArray
				: clients.value
					? [...clients.value, ...clientsArray]
					: clientsArray
			skip.value += take

			if (clientsArray.length < take) {
				canLoadNext.value = false
			}
		}
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const onOpen = async () => {
	try {
		await onFetchClients()
		isClientsListInit.value = true
		await nextTick(() => $input.value?.focusInput())
	} catch (e) {
		console.error(e)
	}
}

const onSearch = async () => {
	$body.value?.scrollTo({ top: 0 })
	canLoadNext.value = true
	skip.value = 0
	await onFetchClients()
}

const debouncedSearch = debounce(onSearch, 500)

const isClientAdded = (clientId: number) => addedIds.value.has(clientId)

const onAttach = async (clientId: number) => {
	try {
		isLoading.value = true
		await store.attachClientToProject(clientId)
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const onDetach = async (clientId: number) => {
	try {
		isLoading.value = true
		await store.detachClientFromProject(clientId)
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const onChangeClientStage = async (stage: boolean, clientId: number) => {
	if (stage) {
		await onAttach(clientId)
	} else {
		await onDetach(clientId)
	}
}

const onChangeClientStageFromName = async (clientId: number) => {
	if (isClientAdded(clientId)) {
		await onDetach(clientId)
	} else {
		await onAttach(clientId)
	}
}

watch(filter, (newVal, oldVal) => {
	if (newVal !== oldVal) {
		debouncedSearch()
	}
})

watch(isClientsListInit, (newValue) => {
	if (newValue) {
		nextTick(() => {
			$body.value?.addEventListener('scroll', async () => {
				if (canLoadNext.value && !isLoading.value) {
					if ($body.value?.scrollTop! + $body.value?.offsetHeight! > ($body.value?.scrollHeight! - 500)) {
						await onFetchClients()
					}
				}
			})
		})
	}
})

onMounted(() => onOpen())
</script>

<style scoped lang="scss">
.add-client-modal {
	display: flex;
	flex-direction: column;
	position: relative;
	height: 100%;
	padding: 12px 0 0 12px;

	&__search {
		display: flex;
		gap: 8px;
		width: 100%;
		margin-bottom: 8px;
		padding-right: 12px;

		&-input {
			width: 100%;
		}
	}

	&__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 8px;
		padding-right: 12px;
		color: #979797;

		&-title {
			font-size: 12px;
			line-height: 16px;
			font-weight: 600;
		}
	}

	&__body {
		display: flex;
		flex: 1;
		flex-direction: column;
		gap: 8px;
		overflow: auto;
		padding-right: 12px;
		padding-bottom: 12px;

		&-item {
			display: flex;
			font-size: 14px;
			line-height: 18px;
			color: #333;
			cursor: pointer;
			transition: color 0.1s;

			&-text {
				font-size: 14px;
				line-height: 18px;
			}

			&:hover {
				text-decoration: underline;
				color: #396742;
			}
		}
	}
}
</style>
